import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuthStore } from '../store/authStore';
import api from '../api/axios';
import { FiUser, FiMail, FiLock, FiShoppingBag, FiBriefcase, FiCheck, FiX, FiArrowRight, FiGlobe, FiEye, FiEyeOff, FiPhone, FiSearch, FiChevronDown } from 'react-icons/fi';
import { t as tr, Lang } from '../utils/translations';
import './AuthPages.css';

const stripArabic = (v: string) => v.replace(/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/g, '');

const COUNTRIES = [
  { code: '+965', iso: 'KW', flag: '🇰🇼', en: 'Kuwait', ar: 'الكويت' },
  { code: '+966', iso: 'SA', flag: '🇸🇦', en: 'Saudi Arabia', ar: 'السعودية' },
  { code: '+971', iso: 'AE', flag: '🇦🇪', en: 'UAE', ar: 'الإمارات' },
  { code: '+973', iso: 'BH', flag: '🇧🇭', en: 'Bahrain', ar: 'البحرين' },
  { code: '+974', iso: 'QA', flag: '🇶🇦', en: 'Qatar', ar: 'قطر' },
  { code: '+968', iso: 'OM', flag: '🇴🇲', en: 'Oman', ar: 'عمان' },
  { code: '+962', iso: 'JO', flag: '🇯🇴', en: 'Jordan', ar: 'الأردن' },
  { code: '+961', iso: 'LB', flag: '🇱🇧', en: 'Lebanon', ar: 'لبنان' },
  { code: '+964', iso: 'IQ', flag: '🇮🇶', en: 'Iraq', ar: 'العراق' },
  { code: '+20', iso: 'EG', flag: '🇪🇬', en: 'Egypt', ar: 'مصر' },
  { code: '+212', iso: 'MA', flag: '🇲🇦', en: 'Morocco', ar: 'المغرب' },
  { code: '+216', iso: 'TN', flag: '🇹🇳', en: 'Tunisia', ar: 'تونس' },
  { code: '+213', iso: 'DZ', flag: '🇩🇿', en: 'Algeria', ar: 'الجزائر' },
  { code: '+249', iso: 'SD', flag: '🇸🇩', en: 'Sudan', ar: 'السودان' },
  { code: '+218', iso: 'LY', flag: '🇱🇾', en: 'Libya', ar: 'ليبيا' },
  { code: '+963', iso: 'SY', flag: '🇸🇾', en: 'Syria', ar: 'سوريا' },
  { code: '+967', iso: 'YE', flag: '🇾🇪', en: 'Yemen', ar: 'اليمن' },
  { code: '+970', iso: 'PS', flag: '🇵🇸', en: 'Palestine', ar: 'فلسطين' },
  { code: '+90', iso: 'TR', flag: '🇹🇷', en: 'Turkey', ar: 'تركيا' },
  { code: '+91', iso: 'IN', flag: '🇮🇳', en: 'India', ar: 'الهند' },
  { code: '+92', iso: 'PK', flag: '🇵🇰', en: 'Pakistan', ar: 'باكستان' },
  { code: '+94', iso: 'LK', flag: '🇱🇰', en: 'Sri Lanka', ar: 'سريلانكا' },
  { code: '+63', iso: 'PH', flag: '🇵🇭', en: 'Philippines', ar: 'الفلبين' },
  { code: '+62', iso: 'ID', flag: '🇮🇩', en: 'Indonesia', ar: 'إندونيسيا' },
  { code: '+880', iso: 'BD', flag: '🇧🇩', en: 'Bangladesh', ar: 'بنغلاديش' },
  { code: '+977', iso: 'NP', flag: '🇳🇵', en: 'Nepal', ar: 'نيبال' },
  { code: '+44', iso: 'GB', flag: '🇬🇧', en: 'United Kingdom', ar: 'بريطانيا' },
  { code: '+1', iso: 'US', flag: '🇺🇸', en: 'United States', ar: 'أمريكا' },
  { code: '+33', iso: 'FR', flag: '🇫🇷', en: 'France', ar: 'فرنسا' },
  { code: '+49', iso: 'DE', flag: '🇩🇪', en: 'Germany', ar: 'ألمانيا' },
  { code: '+39', iso: 'IT', flag: '🇮🇹', en: 'Italy', ar: 'إيطاليا' },
  { code: '+34', iso: 'ES', flag: '🇪🇸', en: 'Spain', ar: 'إسبانيا' },
  { code: '+31', iso: 'NL', flag: '🇳🇱', en: 'Netherlands', ar: 'هولندا' },
  { code: '+46', iso: 'SE', flag: '🇸🇪', en: 'Sweden', ar: 'السويد' },
  { code: '+86', iso: 'CN', flag: '🇨🇳', en: 'China', ar: 'الصين' },
  { code: '+81', iso: 'JP', flag: '🇯🇵', en: 'Japan', ar: 'اليابان' },
  { code: '+82', iso: 'KR', flag: '🇰🇷', en: 'South Korea', ar: 'كوريا الجنوبية' },
  { code: '+60', iso: 'MY', flag: '🇲🇾', en: 'Malaysia', ar: 'ماليزيا' },
  { code: '+65', iso: 'SG', flag: '🇸🇬', en: 'Singapore', ar: 'سنغافورة' },
  { code: '+27', iso: 'ZA', flag: '🇿🇦', en: 'South Africa', ar: 'جنوب أفريقيا' },
  { code: '+55', iso: 'BR', flag: '🇧🇷', en: 'Brazil', ar: 'البرازيل' },
  { code: '+52', iso: 'MX', flag: '🇲🇽', en: 'Mexico', ar: 'المكسيك' },
  { code: '+61', iso: 'AU', flag: '🇦🇺', en: 'Australia', ar: 'أستراليا' },
  { code: '+64', iso: 'NZ', flag: '🇳🇿', en: 'New Zealand', ar: 'نيوزيلندا' },
  { code: '+7', iso: 'RU', flag: '🇷🇺', en: 'Russia', ar: 'روسيا' },
  { code: '+380', iso: 'UA', flag: '🇺🇦', en: 'Ukraine', ar: 'أوكرانيا' },
];

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuthStore();
  const [lang, setLang] = useState<Lang>(() => (localStorage.getItem('cartz_lang') as Lang) || 'en');
  const t = (key: string) => tr(key, lang);
  const isRTL = lang === 'ar';

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    password_confirmation: '',
    store_name: '',
    company_name: '',
  });
  const [countryCode, setCountryCode] = useState('+965');
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const countryRef = useRef<HTMLDivElement>(null);
  const [slugStatus, setSlugStatus] = useState<{ available: boolean; slug: string; message: string } | null>(null);
  const [checkingSlug, setCheckingSlug] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // OTP verification state
  const [step, setStep] = useState<'form' | 'verify-email' | 'verify-phone' | 'creating'>('form');
  const [sessionToken, setSessionToken] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  // Resend countdown
  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => setResendTimer(t => t - 1), 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Close country picker on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (countryRef.current && !countryRef.current.contains(e.target as Node)) setShowCountryPicker(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const checkSlug = useCallback(async (name: string) => {
    if (!name.trim()) {
      setSlugStatus(null);
      return;
    }
    setCheckingSlug(true);
    try {
      const res = await api.post('/check-slug', { slug: name });
      setSlugStatus(res.data);
    } catch {
      setSlugStatus(null);
    } finally {
      setCheckingSlug(false);
    }
  }, []);

  const handleStoreNameChange = (value: string) => {
    value = stripArabic(value);
    setForm({ ...form, store_name: value });
    const timer = setTimeout(() => checkSlug(value), 500);
    return () => clearTimeout(timer);
  };

  const fullPhone = countryCode + form.phone.replace(/\s/g, '');

  // Step 1: Form filled → send email OTP
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (slugStatus && !slugStatus.available) {
      toast.error(isRTL ? 'يرجى اختيار اسم متجر مختلف' : 'Please choose a different store name');
      return;
    }
    if (!form.phone.trim()) {
      toast.error(isRTL ? 'يرجى إدخال رقم الهاتف' : 'Please enter your phone number');
      return;
    }
    setSendingOtp(true);
    try {
      // Check if phone is already registered before starting OTP flow
      await api.post('/check-phone', { phone: fullPhone });
      const res = await api.post('/otp/send', { identifier: form.email, type: 'email', lang, session_token: sessionToken || undefined });
      setSessionToken(res.data.session_token);
      setStep('verify-email');
      setResendTimer(60);
      toast.success(isRTL ? 'تم إرسال رمز التحقق إلى بريدك' : 'Verification code sent to your email');
    } catch (err: any) {
      toast.error(err.response?.data?.message || (isRTL ? 'فشل إرسال رمز التحقق' : 'Failed to send verification code'));
    } finally { setSendingOtp(false); }
  };

  // Verify email OTP → then send phone OTP
  const verifyEmail = async () => {
    setVerifyingOtp(true);
    try {
      const res = await api.post('/otp/verify', { identifier: form.email, type: 'email', code: emailCode, session_token: sessionToken });
      if (res.data.verified) {
        setEmailVerified(true);
        // Now send phone OTP
        await api.post('/otp/send', { identifier: fullPhone, type: 'phone', lang, session_token: sessionToken });
        setStep('verify-phone');
        setResendTimer(60);
        toast.success(isRTL ? 'تم التحقق! رمز التحقق أُرسل لهاتفك' : 'Email verified! Code sent to your phone');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || (isRTL ? 'رمز غير صحيح' : 'Invalid code'));
    } finally { setVerifyingOtp(false); }
  };

  // Verify phone OTP → create account
  const verifyPhone = async () => {
    setVerifyingOtp(true);
    try {
      const res = await api.post('/otp/verify', { identifier: fullPhone, type: 'phone', code: phoneCode, session_token: sessionToken });
      if (res.data.verified) {
        setPhoneVerified(true);
        setStep('creating');
        // Now register
        await register({ ...form, phone: fullPhone, session_token: sessionToken } as any);
        toast.success(isRTL ? 'تم إنشاء المتجر بنجاح!' : 'Store created successfully!');
        navigate('/dashboard');
      }
    } catch (err: any) {
      setStep('verify-phone');
      const errors = err.response?.data?.errors;
      if (errors) {
        Object.values(errors).flat().forEach((msg: any) => toast.error(msg));
      } else {
        toast.error(err.response?.data?.message || (isRTL ? 'رمز غير صحيح' : 'Invalid code'));
      }
    } finally { setVerifyingOtp(false); }
  };

  const resendOtp = async (type: 'email' | 'phone') => {
    setSendingOtp(true);
    try {
      const identifier = type === 'email' ? form.email : fullPhone;
      await api.post('/otp/send', { identifier, type, lang, session_token: sessionToken });
      setResendTimer(60);
      toast.success(isRTL ? 'تم إعادة إرسال الرمز' : 'Code resent');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to resend');
    } finally { setSendingOtp(false); }
  };

  const toggleLang = () => {
    const newLang = lang === 'en' ? 'ar' : 'en';
    setLang(newLang);
    localStorage.setItem('cartz_lang', newLang);
  };

  return (
    <div className="auth-page" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="auth-left">
        <div className="auth-left-top">
          <Link to="/" className="landing-logo">
            <img className="cartz-mark" src="/favicon.svg" alt="" />
            <span className="logo-text">Cartz</span>
          </Link>
          <button className="sf-lang-toggle" onClick={toggleLang} style={{ marginInlineStart: 'auto' }}>
            {lang === 'en' ? 'عربي' : 'English'}
          </button>
        </div>
        <div className="auth-left-content">
          <h2>{t('auth.left.title.register')}</h2>
          <p>{t('auth.left.desc.register')}</p>
          <div className="auth-features">
            <div className="auth-feature"><FiCheck /> {isRTL ? 'نطاق فرعي مجاني (yourname.cartz.cc)' : 'Free subdomain (yourname.cartz.cc)'}</div>
            <div className="auth-feature"><FiCheck /> {isRTL ? 'تصاميم متعددة للمتجر' : 'Multiple store layouts'}</div>
            <div className="auth-feature"><FiCheck /> {isRTL ? 'إدارة الطلبات' : 'Order management'}</div>
            <div className="auth-feature"><FiCheck /> {isRTL ? 'دعم التوصيل والاستلام' : 'Delivery & pickup support'}</div>
          </div>
        </div>
      </div>
      <div className="auth-right">
        <div className="auth-form-wrapper">
          {step === 'form' && <>
          <h1>{t('auth.register.title')}</h1>
          <p className="auth-subtitle">{t('auth.register.subtitle')}</p>
          </>}

          {/* OTP Verification Steps */}
          {step === 'verify-email' && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>📧</div>
              <h2 style={{ margin: '0 0 8px' }}>{isRTL ? 'تحقق من بريدك الإلكتروني' : 'Verify Your Email'}</h2>
              <p style={{ color: 'var(--gray-500)', marginBottom: 24, fontSize: 14 }}>
                {isRTL ? <>أدخل الرمز المرسل إلى <span dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'embed' }}>{form.email}</span></> : `Enter the code sent to ${form.email}`}
              </p>
              <input
                type="text" maxLength={6} autoFocus
                value={emailCode}
                onChange={e => setEmailCode(e.target.value.replace(/\D/g, ''))}
                onKeyDown={e => e.key === 'Enter' && emailCode.length === 6 && verifyEmail()}
                placeholder="000000"
                dir="ltr"
                style={{
                  width: 200, textAlign: 'center', fontSize: 28, fontWeight: 800,
                  letterSpacing: 8, padding: '12px 16px', borderRadius: 10,
                  border: '2px solid var(--gray-200)', fontFamily: 'monospace', margin: '0 auto 16px', display: 'block',
                }}
              />
              <button className="btn btn-primary btn-lg" style={{ width: '100%', maxWidth: 300, margin: '0 auto 12px', display: 'block' }}
                onClick={verifyEmail} disabled={verifyingOtp || emailCode.length !== 6}>
                {verifyingOtp ? (isRTL ? 'جارٍ التحقق...' : 'Verifying...') : (isRTL ? 'تحقق' : 'Verify')}
              </button>
              <button
                onClick={() => resendOtp('email')} disabled={resendTimer > 0 || sendingOtp}
                style={{ background: 'none', border: 'none', color: resendTimer > 0 ? 'var(--gray-400)' : 'var(--primary)', cursor: resendTimer > 0 ? 'default' : 'pointer', fontSize: 13 }}>
                {resendTimer > 0
                  ? (isRTL ? `إعادة إرسال خلال ${resendTimer} ثانية` : `Resend in ${resendTimer}s`)
                  : (isRTL ? 'إعادة إرسال الرمز' : 'Resend code')}
              </button>
              <button onClick={() => setStep('form')} style={{ display: 'block', margin: '16px auto 0', background: 'none', border: 'none', color: 'var(--gray-500)', cursor: 'pointer', fontSize: 13 }}>
                {isRTL ? '← رجوع' : '← Back'}
              </button>
            </div>
          )}

          {step === 'verify-phone' && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>📱</div>
              <h2 style={{ margin: '0 0 8px' }}>{isRTL ? 'تحقق من رقم هاتفك' : 'Verify Your Phone'}</h2>
              <p style={{ color: 'var(--gray-500)', marginBottom: 8, fontSize: 14 }}>
                {isRTL ? <>أدخل الرمز المرسل إلى <span dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'embed' }}>{fullPhone}</span></> : `Enter the code sent to ${fullPhone}`}
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 16 }}>
                <span style={{ padding: '4px 10px', borderRadius: 20, background: '#dcfce7', color: '#16a34a', fontSize: 12, fontWeight: 600 }}>
                  ✓ {isRTL ? 'تم التحقق من البريد' : 'Email verified'}
                </span>
              </div>
              <input
                type="text" maxLength={6} autoFocus
                value={phoneCode}
                onChange={e => setPhoneCode(e.target.value.replace(/\D/g, ''))}
                onKeyDown={e => e.key === 'Enter' && phoneCode.length === 6 && verifyPhone()}
                placeholder="000000"
                dir="ltr"
                style={{
                  width: 200, textAlign: 'center', fontSize: 28, fontWeight: 800,
                  letterSpacing: 8, padding: '12px 16px', borderRadius: 10,
                  border: '2px solid var(--gray-200)', fontFamily: 'monospace', margin: '0 auto 16px', display: 'block',
                }}
              />
              <button className="btn btn-primary btn-lg" style={{ width: '100%', maxWidth: 300, margin: '0 auto 12px', display: 'block' }}
                onClick={verifyPhone} disabled={verifyingOtp || phoneCode.length !== 6}>
                {verifyingOtp ? (isRTL ? 'جارٍ إنشاء متجرك...' : 'Creating your store...') : (isRTL ? 'تحقق وأنشئ المتجر' : 'Verify & Create Store')}
              </button>
              <button
                onClick={() => resendOtp('phone')} disabled={resendTimer > 0 || sendingOtp}
                style={{ background: 'none', border: 'none', color: resendTimer > 0 ? 'var(--gray-400)' : 'var(--primary)', cursor: resendTimer > 0 ? 'default' : 'pointer', fontSize: 13 }}>
                {resendTimer > 0
                  ? (isRTL ? `إعادة إرسال خلال ${resendTimer} ثانية` : `Resend in ${resendTimer}s`)
                  : (isRTL ? 'إعادة إرسال الرمز' : 'Resend code')}
              </button>
            </div>
          )}

          {step === 'creating' && (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🚀</div>
              <h2>{isRTL ? 'جارٍ إنشاء متجرك...' : 'Creating your store...'}</h2>
            </div>
          )}

          {step === 'form' && <form onSubmit={handleFormSubmit} className="auth-form">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('form.firstName')} *</label>
                <div className="input-icon">
                  <FiUser />
                  <input
                    type="text"
                    className="form-input"
                    placeholder={isRTL ? 'محمد' : 'John'}
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: stripArabic(e.target.value) })}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{t('form.lastName')} *</label>
                <div className="input-icon">
                  <FiUser />
                  <input
                    type="text"
                    className="form-input"
                    placeholder={isRTL ? 'أحمد' : 'Doe'}
                    value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: stripArabic(e.target.value) })}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{t('form.storeName')} *</label>
              <div className="input-icon">
                <FiShoppingBag />
                <input
                  type="text"
                  className="form-input"
                  placeholder={isRTL ? 'اسم متجرك' : 'My Awesome Store'}
                  value={form.store_name}
                  onChange={(e) => handleStoreNameChange(e.target.value)}
                  required
                />
              </div>
              {form.store_name && (
                <div className={`slug-status ${slugStatus?.available ? 'available' : 'taken'}`}>
                  {checkingSlug ? (
                    <span className="slug-checking">{isRTL ? 'جارٍ التحقق من التوفر...' : 'Checking availability...'}</span>
                  ) : slugStatus ? (
                    <>
                      {slugStatus.available ? <FiCheck /> : <FiX />}
                      <FiGlobe />
                      <span>{slugStatus.slug}.cartz.cc</span>
                      <span className="slug-msg">{isRTL ? (slugStatus.available ? 'متاح!' : 'مستخدم بالفعل.') : slugStatus.message}</span>
                    </>
                  ) : null}
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">{t('form.email')} *</label>
              <div className="input-icon">
                <FiMail />
                <input
                  type="email"
                  className="form-input"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: stripArabic(e.target.value) })}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{isRTL ? 'رقم الهاتف' : 'Phone Number'} *</label>
              <div style={{ display: 'flex', gap: 0, direction: 'ltr' }} ref={countryRef}>
                {/* Country code selector */}
                <div style={{ position: 'relative' }}>
                  <button
                    type="button"
                    onClick={() => { setShowCountryPicker(!showCountryPicker); setCountrySearch(''); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      padding: '10px 10px', height: '100%',
                      border: '1px solid var(--gray-200)', borderRadius: '8px 0 0 8px',
                      borderRight: 'none', background: 'var(--gray-50)', cursor: 'pointer',
                      fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap',
                    }}>
                    <span style={{ fontSize: 18 }}>{COUNTRIES.find(c => c.code === countryCode)?.flag}</span>
                    <span>{countryCode}</span>
                    <FiChevronDown style={{ fontSize: 12, color: 'var(--gray-400)' }} />
                  </button>
                  {showCountryPicker && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, zIndex: 100,
                      background: 'white', border: '1px solid var(--gray-200)',
                      borderRadius: 10, boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                      width: 280, maxHeight: 300, overflow: 'hidden',
                    }}>
                      <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--gray-100)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', border: '1px solid var(--gray-200)', borderRadius: 6, background: 'var(--gray-50)' }}>
                          <FiSearch style={{ color: 'var(--gray-400)', flexShrink: 0 }} />
                          <input
                            autoFocus
                            value={countrySearch}
                            onChange={e => setCountrySearch(e.target.value)}
                            placeholder={isRTL ? 'بحث...' : 'Search...'}
                            style={{ border: 'none', outline: 'none', background: 'none', width: '100%', fontSize: 13 }}
                          />
                        </div>
                      </div>
                      <div style={{ overflowY: 'auto', maxHeight: 240 }}>
                        {COUNTRIES.filter(c => {
                          if (!countrySearch) return true;
                          const q = countrySearch.toLowerCase();
                          return c.en.toLowerCase().includes(q) || c.ar.includes(q) || c.code.includes(q) || c.iso.toLowerCase().includes(q);
                        }).map(c => (
                          <button
                            key={c.iso}
                            type="button"
                            onClick={() => { setCountryCode(c.code); setShowCountryPicker(false); }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                              padding: '8px 12px', border: 'none', background: countryCode === c.code ? 'var(--gray-50)' : 'white',
                              cursor: 'pointer', fontSize: 13, textAlign: 'left',
                            }}>
                            <span style={{ fontSize: 18 }}>{c.flag}</span>
                            <span style={{ flex: 1 }}>{isRTL ? c.ar : c.en}</span>
                            <span style={{ color: 'var(--gray-400)', fontFamily: 'monospace', fontSize: 12 }}>{c.code}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <input
                  type="tel"
                  className="form-input"
                  placeholder="XXXX XXXX"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^0-9\s]/g, '') })}
                  required
                  dir="ltr"
                  style={{ borderRadius: '0 8px 8px 0', textAlign: 'left', flex: 1 }}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{t('form.companyName')} ({t('form.optional')})</label>
              <div className="input-icon">
                <FiBriefcase />
                <input
                  type="text"
                  className="form-input"
                  placeholder={isRTL ? 'اسم شركتك' : 'Your Company LLC'}
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: stripArabic(e.target.value) })}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('form.password')} *</label>
                <div className="input-icon">
                  <FiLock />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="form-input ltr-input"
                    placeholder={isRTL ? '8 أحرف على الأقل' : 'Min 8 characters'}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: stripArabic(e.target.value) })}
                    required
                    minLength={8}
                  />
                  <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{t('form.confirmPassword')} *</label>
                <div className="input-icon">
                  <FiLock />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    className="form-input ltr-input"
                    placeholder={isRTL ? 'تأكيد كلمة المرور' : 'Confirm password'}
                    value={form.password_confirmation}
                    onChange={(e) => setForm({ ...form, password_confirmation: stripArabic(e.target.value) })}
                    required
                  />
                  <button type="button" className="password-toggle" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                    {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>
            </div>

            <label className="terms-checkbox">
              <input type="checkbox" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} />
              <span>
                {isRTL ? 'أوافق على ' : 'I agree to the '}
                <Link to="/terms" target="_blank">{isRTL ? 'شروط الخدمة' : 'Terms of Service'}</Link>
                {isRTL ? ' و' : ' and '}
                <Link to="/privacy" target="_blank">{isRTL ? 'سياسة الخصوصية' : 'Privacy Policy'}</Link>
              </span>
            </label>

            <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%' }} disabled={sendingOtp || !agreedToTerms || (slugStatus !== null && !slugStatus.available) || !form.phone.trim()}>
              {sendingOtp ? (isRTL ? 'جارٍ الإرسال...' : 'Sending...') : (isRTL ? 'متابعة التحقق' : 'Continue to Verify')} <FiArrowRight style={isRTL ? { transform: 'rotate(180deg)' } : {}} />
            </button>
          </form>}

          <p className="auth-switch">
            {t('auth.register.switch')} <Link to="/login">{t('auth.register.switch.link')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
